<section class="filter-bar" role="region" aria-label="Dashboard filters">
  <div class="filter-bar-control filter-bar-control--date">
    <app-date-range-picker
      [selection]="dateRange()"
      [resetToken]="resetRevision()"
      (selectionChange)="onDateRangeChange($event)"
    />
  </div>

  <!-- Client scoping follows the role: absent for client-portal sessions, and
       absent from the DOM entirely (@if, never CSS hiding — Art. XI-C).

       The route guard is NESTED inside the role guard, not merged with it: they are two
       independent reasons for the same absence, and a client-portal session must render no client
       UI on a route that declares no removal either (SC-007). -->
  @if (isInternalRole() && !isDimensionHidden('client')) {
    <div class="filter-bar-control">
      <app-searchable-select
        name="client"
        label="Client"
        [placeholder]="allClientsPlaceholder()"
        [options]="clientOptions()"
        [value]="filters.draftClientCode()"
        [displayFor]="clientDisplayFor"
        [resetToken]="resetRevision()"
        (valueChange)="onClientChange($event)"
      />
    </div>
  }

  @if (!isDimensionHidden('app')) {
  <div class="filter-bar-control">
    <app-searchable-select
      name="app"
      label="App"
      [placeholder]="OPEN_SET_PLACEHOLDER.app"
      [options]="appOptions()"
      [value]="app()"
      [unspecifiedLabel]="UNSPECIFIED_LABEL"
      [unspecifiedSelected]="appUnspecified()"
      [disabled]="isDimensionDisabled('app')"
      [disabledReason]="disabledReasonFor('app') ?? DEFAULT_DISABLED_REASON"
      [resetToken]="resetRevision()"
      (valueChange)="onOpenSetChange('app', $event)"
      (unspecifiedChange)="onUnspecifiedChosen('app')"
    />
  </div>
  }

  @if (!isDimensionHidden('model')) {
  <div class="filter-bar-control">
    <app-searchable-select
      name="model"
      label="Model"
      [placeholder]="OPEN_SET_PLACEHOLDER.model"
      [options]="modelOptions()"
      [value]="model()"
      [unspecifiedLabel]="UNSPECIFIED_LABEL"
      [unspecifiedSelected]="modelUnspecified()"
      [disabled]="isDimensionDisabled('model')"
      [disabledReason]="disabledReasonFor('model') ?? DEFAULT_DISABLED_REASON"
      [resetToken]="resetRevision()"
      (valueChange)="onOpenSetChange('model', $event)"
      (unspecifiedChange)="onUnspecifiedChosen('model')"
    />
  </div>
  }

  @if (!isDimensionHidden('provider')) {
  <div class="filter-bar-control">
    <app-searchable-select
      name="provider"
      label="Provider"
      [placeholder]="OPEN_SET_PLACEHOLDER.provider"
      [options]="providerOptions()"
      [value]="provider()"
      [unspecifiedLabel]="UNSPECIFIED_LABEL"
      [unspecifiedSelected]="providerUnspecified()"
      [disabled]="isDimensionDisabled('provider')"
      [disabledReason]="disabledReasonFor('provider') ?? DEFAULT_DISABLED_REASON"
      [resetToken]="resetRevision()"
      (valueChange)="onOpenSetChange('provider', $event)"
      (unspecifiedChange)="onUnspecifiedChosen('provider')"
    />
  </div>
  }

  <!-- GH-34 §1 D-3: `user` and `status` are HIDDEN rather than disabled, wherever they are
       declared, and after that change every dashboard route declares them. That is deliberate and
       has a consequence worth stating at the site: `filter-bar.component.html` is the only host of
       `app-user-filter-input` and `app-status-multi-select` in the application, so neither renders
       anywhere today.

       They are kept, not deleted. `FilterScopeService` still holds both dimensions and the URL
       projection still reads and writes both, so a value inherited from a shared link is carried,
       declared in the page's prose, and clearable by Reset (D-1). Reversal remains one string.

       They are NOT disabled, and must not be: a disabled control is a promise (D-2/D-5). `user` is
       a recorded refusal — the rollup stores one-way hashes that can answer *how many* distinct
       users and never *which* — and `status` has no rollup dimension row at all. Greying them
       would advertise two capabilities that are not coming. -->
  @if (!isDimensionHidden('user')) {
  <div class="filter-bar-control">
    <app-user-filter-input
      [value]="user()"
      [resetToken]="resetRevision()"
      (commitValue)="onUserCommit($event)"
    />
  </div>
  }

  @if (!isDimensionHidden('status')) {
  <div class="filter-bar-control">
    <app-status-multi-select
      [value]="status()"
      [resetToken]="resetRevision()"
      (valueChange)="onStatusChange($event)"
    />
  </div>
  }

  <div class="filter-bar-control filter-bar-control--reset">
    <button type="button" class="btn btn-link filter-reset" data-testid="filter-reset" (click)="reset()">
      <fa-icon [icon]="resetIcon" aria-hidden="true" />
      Reset
    </button>
  </div>

  <!-- AITH-45 D-08: freshness lives beside the controls that change what is displayed, not in the
       top bar. Rendered only where a page has actually published a data instant, so destinations
       without one (and those without a filter bar at all) show nothing. -->
  @if (activePageData.producedAt() !== null) {
    <div class="filter-bar-freshness">
      <app-freshness-indicator
        [producedAt]="activePageData.producedAt()"
        [connectionState]="realtimeStatus.state()"
      />
    </div>
  }
</section>
